import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Lock, UserPlus, Play, BookOpen, Share2, Check, Bookmark, BadgeCheck } from "lucide-react";
import { STORIES, NARRATORS, chapterLabel } from "../data/book";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import { loadProgress, saveProgress, loadBookmarks, saveBookmark, deleteBookmark } from "../progress";
import api, { formatApiError } from "../api";
import { track } from "../telemetry";
import TeaserPlayer from "../components/TeaserPlayer";
import Reveal from "../components/Reveal";

const toParagraphs = (text) => {
  const sentences = text.match(/[^.!?…]+[.!?…]+["”]?\s*/g) || [text];
  const paras = [];
  for (let i = 0; i < sentences.length; i += 5) paras.push(sentences.slice(i, i + 5).join(" ").trim());
  return paras;
};

const Story = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const story = STORIES.find((s) => s.id === Number(id));
  const [full, setFull] = useState(null); // null loading | {text} | {gate: 'register'|'pay'}
  const [payError, setPayError] = useState("");
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("v") === "read" ? "read" : "watch");
  const [savedPct, setSavedPct] = useState(0);
  const [livePct, setLivePct] = useState(0);
  const [bookmarks, setBookmarks] = useState([]);
  const [bmSaved, setBmSaved] = useState(false);
  const [bmNoteOpen, setBmNoteOpen] = useState(false);
  const [bmNote, setBmNote] = useState("");
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentMsg, setCommentMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoMsg, setPromoMsg] = useState("");
  const [promoOk, setPromoOk] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [promoCopied, setPromoCopied] = useState(false);

  const jumpTo = (percent) => {
    const el = readRef.current;
    if (!el) return;
    const top = el.offsetTop + (el.offsetHeight * percent) / 100 - window.innerHeight / 2;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const addBookmark = async (note = "") => {
    const bm = await saveBookmark(user, Number(id), livePct, note);
    setBookmarks((b) => [bm, ...b]);
    setBmSaved(true);
    setBmNoteOpen(false);
    setBmNote("");
    setTimeout(() => setBmSaved(false), 2000);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setCommentMsg(null);
    try {
      await api.post("/comments", { chapter_id: Number(id), text: commentText });
      setCommentText("");
      setCommentMsg({ ok: true, text: t("comments_pending") });
    } catch (err) {
      if (err?.response?.data?.detail === "email_not_verified") {
        setCommentMsg({ ok: false, text: t("verify_needed"), verify: true });
      } else {
        setCommentMsg({ ok: false, text: formatApiError(err) });
      }
    }
  };

  const removeBookmark = async (bmId) => {
    await deleteBookmark(user, Number(id), bmId);
    setBookmarks((b) => b.filter((x) => x.bm_id !== bmId));
  };

  const redeemPromo = async () => {
    setPromoMsg("");
    if (!user) {
      navigate(`/accedi?return=/storia/${id}`);
      return;
    }
    try {
      const { data: chk } = await api.post("/promo/check", { code: promoCode });
      if (chk.discount_percent > 0) {
        setDiscount({ code: chk.code, pct: chk.discount_percent });
        navigator.clipboard?.writeText(chk.code);
        setPromoCopied(true);
        setTimeout(() => setPromoCopied(false), 2000);
        return;
      }
      await api.post("/promo", { code: promoCode });
      setPromoOk(true);
      const { data } = await api.get(`/chapters/${id}/text?lang=${lang}`);
      setFull({ text: data.text, en_pending: data.en_pending });
    } catch (e) {
      setPromoMsg(formatApiError(e, t("promo_login")));
    }
  };
  const readRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    loadProgress(user).then((p) => {
      const c = p.chapters?.[String(id)];
      setSavedPct(c?.percent || 0);
    });
    loadBookmarks(user, Number(id)).then(setBookmarks);
    api.get(`/comments/${id}`).then(({ data }) => setComments(data)).catch(() => setComments([]));
    saveProgress(user, Number(id), 0, true);
  }, [id, user]);

  useEffect(() => {
    if (mode !== "read" || !full?.text) return;
    const onScroll = () => {
      const el = readRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const pct = Math.min(100, Math.max(0, ((vh - r.top) / (r.height + vh * 0.5)) * 100));
      setLivePct((prev) => (Math.abs(prev - pct) > 1 ? pct : prev));
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => saveProgress(user, Number(id), pct, true), 800);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [mode, full, user, id]);

  const resume = () => {
    const el = readRef.current;
    if (!el) return;
    const top = el.offsetTop + (el.offsetHeight * savedPct) / 100 - window.innerHeight / 2;
    window.scrollTo({ top, behavior: "smooth" });
  };

  useEffect(() => {
    setFull(null);
    api.get(`/chapters/${id}/text?lang=${lang}`)
      .then(({ data }) => setFull({ text: data.text, en_pending: data.en_pending }))
      .catch((e) => {
        const code = e?.response?.status;
        setFull({ gate: code === 402 ? "pay" : "register" });
      });
  }, [id, user, lang]);

  if (!story) return <Navigate to="/storie" replace />;

  const nar = NARRATORS[story.narrator];
  const prev = STORIES[story.id - 1];
  const next = STORIES[story.id + 1];
  const paragraphs = full?.text ? toParagraphs(full.text) : [];
  const parts = [];
  for (let i = 0; i < paragraphs.length; i += 6) parts.push(paragraphs.slice(i, i + 6));

  const unlock = async () => {
    if (!user) {
      navigate(`/accedi?return=/storia/${id}`);
      return;
    }
    setPayError("");
    track("unlock_click", `cap ${id}${discount ? ` sconto ${discount.pct}%` : ""}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "unlock_full",
        origin_url: window.location.origin,
        promo_code: discount?.code || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e?.response?.data?.detail === "email_not_verified") {
        navigate("/verifica-email");
        return;
      }
      setPayError(formatApiError(e));
    }
  };

  return (
    <main className="pt-16" data-testid="story-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse 70% 55% at 50% 0%, ${nar.dim}, transparent 70%)` }} />
        <span className="absolute -top-10 right-0 font-display font-extrabold text-[38vw] lg:text-[24rem] leading-none text-white/[0.04] select-none pointer-events-none">
          {story.num}
        </span>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-20 pb-14">
          <Reveal>
            <Link to="/storie" data-testid="story-back" className="inline-flex items-center gap-2 font-mono2 text-xs tracking-[0.25em] uppercase text-mute hover:text-ember transition-colors mb-10">
              <ArrowLeft className="w-4 h-4" /> {t("story_back")}
            </Link>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border"
                style={{ color: nar.color, borderColor: `${nar.color}55`, background: nar.dim }} data-testid="story-narrator">
                {nar[lang] ?? nar.it}
              </span>
              <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {story.place}
              </span>
              <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-gold">{story.bpm} BPM</span>
              <button
                data-testid="story-share-button"
                onClick={() => {
                  const url = window.location.href;
                  const payload = { title: `Una vita da DJ — ${chapterLabel(story, lang)}`, text: nar[lang] ?? nar.it, url };
                  if (navigator.share) navigator.share(payload).catch(() => {});
                  else { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
                }}
                className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute hover:text-ember transition-colors flex items-center gap-1.5"
              >
                {copied ? <><Check className="w-3 h-3 text-gold" /> {t("share_copied")}</> : <><Share2 className="w-3 h-3" /> {t("share")}</>}
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/ig/${story.id}.jpg`}
                download={`una-vita-da-dj-capitolo-${story.num}.jpg`}
                data-testid="story-ig-download"
                className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute hover:text-gold transition-colors flex items-center gap-1.5"
              >
                <Share2 className="w-3 h-3 rotate-180" /> {t("card_download")}
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/reels/${story.id}.mp4`}
                download={`una-vita-da-dj-reel-${story.num}.mp4`}
                data-testid="story-reel-download"
                className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute hover:text-viola transition-colors flex items-center gap-1.5"
              >
                <Play className="w-3 h-3" /> Reel 9:16
              </a>
            </div>
            <h1 className="font-display font-extrabold uppercase text-4xl sm:text-6xl tracking-tight leading-none" data-testid="story-title">
              {chapterLabel(story, lang)}
            </h1>
            <p className="font-serif-it text-xl text-mist mt-3">{t("chapter_word")} {story.num} · p. {story.page}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-10">
        <Reveal>
          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-card p-2" data-testid="story-mode-toggle">
            <button
              data-testid="mode-watch-button"
              onClick={() => setMode("watch")}
              className={`rounded-xl px-4 py-4 text-left transition-all duration-300 ${mode === "watch" ? "bg-ember text-black" : "text-mist hover:text-white"}`}
            >
              <span className="flex items-center gap-2 font-display font-bold uppercase tracking-wide"><Play className="w-4 h-4" /> {t("watch_mode")}</span>
              <span className={`block text-[11px] mt-1 ${mode === "watch" ? "text-black/70" : "text-mute"}`}>{t("watch_desc")}</span>
            </button>
            <button
              data-testid="mode-read-button"
              onClick={() => setMode("read")}
              className={`rounded-xl px-4 py-4 text-left transition-all duration-300 ${mode === "read" ? "bg-gold text-black" : "text-mist hover:text-white"}`}
            >
              <span className="flex items-center gap-2 font-display font-bold uppercase tracking-wide"><BookOpen className="w-4 h-4" /> {t("read_mode")}</span>
              <span className={`block text-[11px] mt-1 ${mode === "read" ? "text-black/70" : "text-mute"}`}>{t("read_desc")}</span>
            </button>
          </div>
        </Reveal>
      </section>

      {mode === "read" && full?.text && (
        <div className="fixed top-16 inset-x-0 z-40 h-1 bg-white/10" data-testid="reading-progress-bar">
          <div className="h-full transition-[width] duration-150" style={{ width: `${livePct}%`, background: "linear-gradient(90deg, #FF5500, #FFB800)" }} />
        </div>
      )}

      {mode === "watch" && (
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <TeaserPlayer story={story} limited={full?.gate === "register" ? "register" : full?.gate === "pay" ? "pay" : null} />
        </Reveal>
      </section>
      )}

      {mode === "read" && (
      <section ref={readRef} className="max-w-3xl mx-auto px-5 sm:px-8 pb-20" data-testid="story-reading-section">
        {savedPct > 5 && savedPct < 95 && (
          <button
            onClick={resume}
            data-testid="resume-reading-button"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-5 py-2.5 font-mono2 text-[11px] tracking-[0.2em] uppercase text-gold hover:bg-gold hover:text-black transition-colors"
          >
            {t("resume_from")} {savedPct}% →
          </button>
        )}
        <Reveal>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-7">
            {full?.text ? t("story_full") : t("story_excerpt")}
          </p>
          {full?.text && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="reading-toolbar">
                {parts.length > 1 && parts.map((_, pi) => (
                  <button
                    key={pi}
                    data-testid={`part-chip-${pi}`}
                    onClick={() => document.getElementById(`part-${pi}`)?.scrollIntoView({ behavior: "smooth" })}
                    className="rounded-full border border-white/15 px-3.5 py-1.5 font-mono2 text-[10px] tracking-[0.15em] uppercase text-mist hover:border-ember hover:text-ember transition-colors"
                  >
                    {t("read_part")} {pi + 1}
                  </button>
                ))}
                <button
                  data-testid="bookmark-add-button"
                  onClick={() => setBmNoteOpen(!bmNoteOpen)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono2 text-[10px] tracking-[0.15em] uppercase transition-colors ${bmSaved ? "border-gold bg-gold text-black" : "border-gold/40 text-gold hover:bg-gold hover:text-black"}`}
                >
                  <Bookmark className="w-3 h-3" /> {bmSaved ? t("bookmark_saved") : t("bookmark_add")}
                </button>
              </div>
              {bmNoteOpen && (
                <div className="flex flex-col sm:flex-row gap-2 mb-8" data-testid="bookmark-note-form">
                  <input
                    data-testid="bookmark-note-input"
                    value={bmNote}
                    onChange={(e) => setBmNote(e.target.value)}
                    maxLength={280}
                    placeholder={t("bm_note_placeholder")}
                    className="flex-1 rounded-full border border-gold/40 bg-black/40 px-5 py-2.5 text-sm placeholder:text-mute focus:border-gold focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button data-testid="bookmark-note-save" onClick={() => addBookmark(bmNote)}
                      className="rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-ember transition-colors">
                      {t("bm_save")}
                    </button>
                    <button data-testid="bookmark-note-skip" onClick={() => addBookmark("")}
                      className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-mono2 uppercase tracking-wider text-mute hover:text-white transition-colors">
                      {t("bm_skip")}
                    </button>
                  </div>
                </div>
              )}
              {bookmarks.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="bookmarks-list">
                  <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-mute">{t("bookmarks")}</span>
                  {bookmarks.map((b) => (
                    <span key={b.bm_id} title={b.note || undefined} className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 pl-3 pr-1.5 py-1 text-xs text-gold">
                      <button onClick={() => jumpTo(b.percent)} data-testid={`bookmark-jump-${b.bm_id}`} className="font-mono2 hover:text-white transition-colors">
                        {b.percent}%{b.note ? " · ✎" : ""}
                      </button>
                      <button onClick={() => removeBookmark(b.bm_id)} data-testid={`bookmark-del-${b.bm_id}`} className="w-5 h-5 rounded-full text-mute hover:text-red-400 transition-colors" aria-label="delete">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
          {!full?.text && (
            <p className="dropcap text-lg sm:text-xl leading-relaxed text-white/90">
              {story[lang] ?? story.it}
            </p>
          )}
          {full?.text && (
            <div className="space-y-6" data-testid="story-fulltext">
              {full.en_pending && (
                <p className="font-mono2 text-[11px] tracking-wider uppercase text-gold border border-gold/40 bg-gold/10 rounded-lg px-4 py-2.5" data-testid="en-pending-note">
                  {t("en_pending")}
                </p>
              )}
              {parts.map((group, pi) => (
                <div key={pi} id={`part-${pi}`} className="space-y-6 scroll-mt-28">
                  {group.map((p, i) => (
                    <p key={i} className={`leading-relaxed text-white/90 ${pi === 0 && i === 0 ? "dropcap text-lg sm:text-xl" : "text-base sm:text-lg"}`}>{p}</p>
                  ))}
                </div>
              ))}
            </div>
          )}

          {full?.gate === "register" && (
            <div className="rounded-2xl border border-viola/50 bg-viola/10 p-8 sm:p-10 text-center mt-4" data-testid="gate-register">
              <UserPlus className="w-8 h-8 text-viola mx-auto mb-4" />
              <h3 className="font-display font-bold text-2xl mb-3">{t("gate_reg_title")}</h3>
              <p className="text-mist mb-7 max-w-md mx-auto">{story.id <= 5 ? t("gate_reg_sub") : t("gate_reg_sub_locked")}</p>
              <Link to={`/accedi?mode=register&return=/storia/${id}`} data-testid="gate-register-button"
                className="inline-flex rounded-full bg-ember px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                {t("gate_reg_btn")}
              </Link>
            </div>
          )}

          {full?.gate === "pay" && (
            <div className="rounded-2xl border border-ember/50 bg-ember/5 p-8 sm:p-10 text-center mt-4" data-testid="gate-pay">
              <Lock className="w-8 h-8 text-ember mx-auto mb-4" />
              <h3 className="font-display font-bold text-2xl mb-3">{t("gate_pay_title")}</h3>
              <p className="text-mist mb-7 max-w-md mx-auto">{t("gate_pay_sub")}</p>
              <button onClick={unlock} data-testid="gate-unlock-button"
                className="inline-flex rounded-full bg-ember px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors glow-orange">
                {discount
                  ? t("promo_pay_discounted").replace("{price}", (10 * (1 - discount.pct / 100)).toFixed(2).replace(".", ",")).replace("{pct}", discount.pct)
                  : t("gate_pay_btn")}
              </button>
              {discount && (
                <p className="mt-4" data-testid="promo-discount-applied">
                  <span className="block text-xs text-mute mb-2">{t("promo_discount_on").replace("{pct}", discount.pct)}</span>
                  <button
                    data-testid="promo-discount-chip"
                    onClick={() => { navigator.clipboard?.writeText(discount.code); setPromoCopied(true); setTimeout(() => setPromoCopied(false), 2000); }}
                    className="inline-flex items-center gap-2 rounded-full border border-dashed border-gold/60 bg-gold/10 px-4 py-1.5 text-xs font-mono2 tracking-widest text-gold hover:bg-gold hover:text-black transition-colors"
                  >
                    {discount.code} · −{discount.pct}% · {promoCopied ? t("code_copied") : t("code_copy")}
                  </button>
                </p>
              )}
              {payError && <p className="text-sm text-red-400 mt-4" data-testid="gate-pay-error">{payError}</p>}
              <div className="mt-8 max-w-sm mx-auto" data-testid="promo-box">
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute mb-3">{t("promo_label")}</p>
                {promoOk ? (
                  <p className="text-gold font-semibold" data-testid="promo-success">{t("promo_ok")}</p>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="promo-code-input"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder={t("promo_placeholder")}
                      className="flex-1 rounded-full border border-white/15 bg-black/40 px-5 py-3 text-sm placeholder:text-mute focus:border-gold focus:outline-none transition-colors"
                    />
                    <button
                      data-testid="promo-redeem-button"
                      onClick={redeemPromo}
                      className="rounded-full border border-gold/50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gold hover:bg-gold hover:text-black transition-colors"
                    >
                      {t("promo_btn")}
                    </button>
                  </div>
                )}
                {promoMsg && <p className="text-sm text-red-400 mt-3" data-testid="promo-error">{promoMsg}</p>}
              </div>
              <p className="mt-6">
                <Link to="/sostieni" data-testid="gate-donate-link" className="text-sm text-gold hover:text-ember transition-colors font-serif-it text-base">
                  {t("gate_donate_hint")} →
                </Link>
              </p>
            </div>
          )}
        </Reveal>

        <Reveal>
          <div className="mt-14 pt-10 border-t border-white/10" data-testid="comments-section">
            <h3 className="font-display font-bold text-xl mb-2">{t("comments_title")}</h3>
            <p className="text-mute text-xs mb-6">{t("comments_sub")}</p>
            {(comments || []).length === 0 && <p className="text-mute text-sm mb-6">{t("comments_empty")}</p>}
            <div className="space-y-4 mb-8">
              {(comments || []).map((c) => (
                <div key={c.comment_id} className="rounded-xl border border-white/10 bg-card px-5 py-4" data-testid={`comment-${c.comment_id}`}>
                  <p className="text-sm text-white/90 leading-relaxed mb-2">{c.text}</p>
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-mute flex items-center gap-1.5">
                    {c.user_name}
                    {c.verified && (
                      <span title={t("comments_verified")} data-testid={`comment-verified-${c.comment_id}`} className="inline-flex items-center text-gold">
                        <BadgeCheck className="w-3.5 h-3.5" />
                      </span>
                    )}
                    · {new Date(c.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                  </p>
                </div>
              ))}
            </div>
            {user ? (
              <form onSubmit={submitComment} data-testid="comment-form">
                <textarea
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder={t("comments_placeholder")}
                  className="w-full rounded-xl border border-white/15 bg-black/40 px-5 py-4 text-sm placeholder:text-mute focus:border-ember focus:outline-none resize-none mb-3"
                />
                {commentMsg && (
                  <p className={`text-sm mb-3 ${commentMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="comment-msg">
                    {commentMsg.text}
                    {commentMsg.verify && (
                      <Link to="/verifica-email" data-testid="comment-verify-link" className="ml-2 underline text-ember hover:text-gold">{t("verify_link")}</Link>
                    )}
                  </p>
                )}
                <button data-testid="comment-submit" className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                  {t("comments_send")}
                </button>
              </form>
            ) : (
              <Link to={`/accedi?return=/storia/${id}`} data-testid="comment-login-link" className="inline-flex items-center gap-2 text-sm text-ember hover:text-gold transition-colors">
                {t("comments_login")} →
              </Link>
            )}
          </div>
        </Reveal>
      </section>
      )}

      <nav className="max-w-4xl mx-auto px-5 sm:px-8 pb-24 grid grid-cols-2 gap-4" data-testid="story-pagination">
        {prev ? (
          <Link to={`/storia/${prev.id}`} data-testid="story-prev" className="group block rounded-xl border border-white/10 bg-card p-5 hover:border-ember/50 transition-colors">
            <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute flex items-center gap-2 mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("story_prev")}
            </span>
            <span className="font-display font-bold group-hover:text-ember transition-colors">{chapterLabel(prev, lang)}</span>
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`/storia/${next.id}`} data-testid="story-next" className="group block rounded-xl border border-white/10 bg-card p-5 text-right hover:border-ember/50 transition-colors">
            <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute flex items-center justify-end gap-2 mb-2">
              {t("story_next")} <ArrowRight className="w-3.5 h-3.5" />
            </span>
            <span className="font-display font-bold group-hover:text-ember transition-colors">{chapterLabel(next, lang)}</span>
          </Link>
        ) : <span />}
      </nav>
    </main>
  );
};

export default Story;
